import { useCallback, useEffect, useState } from "react";
import type {
  MessageBalanceBlockedJobsResponse,
  OrganizationMessageBalanceResponse,
} from "@multi-chat/shared";
import {
  discardOrganizationBlockedJobs,
  getOrganizationBlockedJobs,
  getOrganizationMessageBalance,
  resumeOrganizationBlockedJobs,
} from "../../api/organizations-api.js";

export interface OrganizationMessageBalanceHookResult {
  isAgent: boolean;
  balance: OrganizationMessageBalanceResponse | null;
  blockedJobs: MessageBalanceBlockedJobsResponse | null;
  loading: boolean;
  error: unknown;
  refresh: () => Promise<void>;
  resumeBlockedJobs: (expectedCount: number, fingerprint: string) => Promise<{ success: true }>;
  discardBlockedJobs: (expectedCount: number, fingerprint: string) => Promise<{ success: true }>;
}

export function useOrganizationMessageBalance(
  organizationId: number | null | undefined,
  role: string | undefined,
): OrganizationMessageBalanceHookResult {
  const isAgent = role === "AGENT";

  const [balance, setBalance] = useState<OrganizationMessageBalanceResponse | null>(null);
  const [blockedJobs, setBlockedJobs] = useState<MessageBalanceBlockedJobsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(!isAgent && Boolean(organizationId));
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    if (!organizationId || isAgent) {
      setBalance(null);
      setBlockedJobs(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [balResult, blockedResult] = await Promise.allSettled([
        getOrganizationMessageBalance(organizationId),
        getOrganizationBlockedJobs(organizationId),
      ]);

      if (balResult.status === "fulfilled") {
        setBalance(balResult.value);
      } else {
        setBalance(null);
        setError(balResult.reason);
      }

      if (blockedResult.status === "fulfilled") {
        setBlockedJobs(blockedResult.value);
      } else {
        setBlockedJobs(null);
      }
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  }, [organizationId, isAgent]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const resume = useCallback(
    async (expectedCount: number, fingerprint: string) => {
      if (!organizationId) throw new Error("No organization ID");
      const result = await resumeOrganizationBlockedJobs(organizationId, {
        expectedCount,
        fingerprint,
      });
      await refresh();
      return result;
    },
    [organizationId, refresh],
  );

  const discard = useCallback(
    async (expectedCount: number, fingerprint: string) => {
      if (!organizationId) throw new Error("No organization ID");
      const result = await discardOrganizationBlockedJobs(organizationId, {
        expectedCount,
        fingerprint,
      });
      await refresh();
      return result;
    },
    [organizationId, refresh],
  );

  return {
    isAgent,
    balance,
    blockedJobs,
    loading,
    error,
    refresh,
    resumeBlockedJobs: resume,
    discardBlockedJobs: discard,
  };
}
